import {
  QueryClient,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { AxiosError } from 'axios';
import { GetSalesforceBundleNameResponse } from '@weave/schema-gen-ts/dist/schemas/package-service/v1/package.pb';
import {
  HandoffHubAPI,
  BatchCreateHandoffSnapshotsByLocationIDsRequest,
  BatchCreateHandoffSnapshotsByLocationIDsResponse,
  BatchSubmitHandoffRecordsRequest,
  BatchSubmitHandoffRecordsResponse,
  BulkUpdateHandoffSnapshotRecordEntry,
  BulkUpdateHandoffSnapshotRecordsRequest,
  BulkUpdateHandoffSnapshotRecordsResponse,
  CreateHandoffRequest,
  GetHandoffSnapshotsByGroupIDRequest,
  GetHandoffSnapshotsByGroupIDResponse,
} from '@weave/schema-gen-ts/dist/schemas/insys/onboarding/v1/handoff-hub/handoff_hub.pb';
import { authorizedFetch } from 'helpers/utils';
import { CustomAxios } from 'redux/axios';
import { queryKeys } from 'components/onboarding/handoff-hub/handoff-hub.queries';
import { HandoffTypes } from '../../handoff-metrics/handoff.constants';
import { MetricResult } from '../../handoff-metrics/handoff-metrics.types';
import {
  UnifyLocationDetails,
  UnifyLocationRow,
} from '../components/unify-locations-table/unify-locations-table.types';

export const UnifyHandoffQueryKeys = {
  base: ['unify-handoff'] as const,
  location: (locationId: string) => [...UnifyHandoffQueryKeys.base, locationId] as const,
  item: (locationId: string, key: string) =>
    [...UnifyHandoffQueryKeys.base, locationId, key] as const,
  getHandoffSnapshotsByGroupID: (request: GetHandoffSnapshotsByGroupIDRequest) =>
    [...UnifyHandoffQueryKeys.base, 'snapshots-by-group-id', request.groupId] as const,
};

export interface SafeQueryResult<T> {
  data: T | undefined;
  error: Error | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

export type {
  BatchCreateHandoffSnapshotsByLocationIDsRequest,
  BatchCreateHandoffSnapshotsByLocationIDsResponse,
  BatchSubmitHandoffRecordsRequest,
  BatchSubmitHandoffRecordsResponse,
  BulkUpdateHandoffSnapshotRecordEntry,
  BulkUpdateHandoffSnapshotRecordsRequest,
  BulkUpdateHandoffSnapshotRecordsResponse,
  CreateHandoffRequest,
  GetHandoffSnapshotsByGroupIDRequest,
  GetHandoffSnapshotsByGroupIDResponse,
};

const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const DEFAULT_CACHE_TIME = 10 * 60 * 1000; // 10 minutes

export const useGetHandoffSnapshotsByGroupIDQuery = <
  TData = GetHandoffSnapshotsByGroupIDResponse
>(
  request: GetHandoffSnapshotsByGroupIDRequest,
  options?: UseQueryOptions<GetHandoffSnapshotsByGroupIDResponse, AxiosError<any>, TData>
) => {
  return useQuery({
    queryKey: UnifyHandoffQueryKeys.getHandoffSnapshotsByGroupID(request),
    queryFn: () => HandoffHubAPI.GetHandoffSnapshotsByGroupID(authorizedFetch(), request),
    retry: false,
    ...options,
    enabled: !!request.groupId && (options?.enabled ?? true),
  });
};

export const useBatchCreateHandoffSnapshotsByLocationIDsMutation = (
  options?: UseMutationOptions<
    BatchCreateHandoffSnapshotsByLocationIDsResponse,
    AxiosError<any>,
    BatchCreateHandoffSnapshotsByLocationIDsRequest
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BatchCreateHandoffSnapshotsByLocationIDsRequest) =>
      HandoffHubAPI.BatchCreateHandoffSnapshotsByLocationIDs(authorizedFetch(), request),
    ...options,
    onSuccess: (data, variables, context) => {
      if (data?.groupId) {
        queryClient.invalidateQueries(
          UnifyHandoffQueryKeys.getHandoffSnapshotsByGroupID({ groupId: data.groupId })
        );
      }
      variables.handoffs?.forEach((h) => {
        if (h.locationId) {
          queryClient.invalidateQueries(queryKeys.handoffHubSnapshots(h.locationId));
        }
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useBatchSubmitHandoffRecordsMutation = (
  options?: UseMutationOptions<
    BatchSubmitHandoffRecordsResponse,
    AxiosError<any>,
    BatchSubmitHandoffRecordsRequest
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BatchSubmitHandoffRecordsRequest) =>
      HandoffHubAPI.BatchSubmitHandoffRecords(authorizedFetch(), request),
    ...options,
    onSuccess: (data, variables, context) => {
      // Invalidate snapshots for each submitted location
      variables.submissions?.forEach((submission) => {
        if (submission.locationId) {
          queryClient.invalidateQueries(
            queryKeys.handoffHubSnapshots(submission.locationId)
          );
        }
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useBulkUpdateHandoffRecordsMutation = (
  options?: UseMutationOptions<
    BulkUpdateHandoffSnapshotRecordsResponse,
    AxiosError<any>,
    BulkUpdateHandoffSnapshotRecordsRequest
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BulkUpdateHandoffSnapshotRecordsRequest) =>
      HandoffHubAPI.BulkUpdateHandoffRecords(authorizedFetch(), request),
    ...options,
    onSuccess: (data, variables, context) => {
      // Invalidate group snapshots cache if groupId is present on any entry
      const sampleGroupId = variables.entries?.find(
        (entry) => entry.handoffSnapshot?.groupId
      )?.handoffSnapshot?.groupId;

      if (sampleGroupId) {
        queryClient.invalidateQueries(
          UnifyHandoffQueryKeys.getHandoffSnapshotsByGroupID({ groupId: sampleGroupId })
        );
      }

      variables.entries?.forEach((entry) => {
        if (entry.handoffSnapshot?.locationId) {
          queryClient.invalidateQueries(
            queryKeys.handoffHubSnapshots(entry.handoffSnapshot.locationId)
          );
        }
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const fetchSalesforceBundle = async (
  queryClient: QueryClient,
  locationId: string
): Promise<GetSalesforceBundleNameResponse> => {
  return queryClient.fetchQuery({
    queryKey: UnifyHandoffQueryKeys.item(locationId, 'salesforce-bundle'),
    queryFn: async () => {
      const resp = await CustomAxios.get<GetSalesforceBundleNameResponse>(
        `/package-service/v1/salesforce-bundle-name`,
        { headers: { 'Location-Id': locationId } }
      );
      return resp?.data;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
  });
};

export const buildBatchCreatePayload = (
  locs: UnifyLocationRow[],
  detailsMap: Record<string, UnifyLocationDetails>
): CreateHandoffRequest[] => {
  return locs.map((loc) => {
    const details = detailsMap[loc.locationId];
    return {
      locationId: loc.locationId,
      pointOfContact: details?.pocEmail || '',
      notes: {
        disclaimerTypeSent: details?.notes?.disclaimerTypeSent || '',
        routerMakeAndModel: details?.notes?.routerMakeAndModel || '',
        officeExperience: details?.notes?.officeExperience || '',
      },
      handoffType: HandoffTypes.OnboardingHandoffUnify,
      handoffName: 'Onboarding Handoff (Unify)',
    };
  });
};

export const buildBulkUpdatePayload = (
  locs: UnifyLocationRow[],
  detailsMap: Record<string, UnifyLocationDetails>,
  currentGroupId?: string,
  currentHandoffIdMap: Record<string, string> = {},
  metricsByLocation?: Record<string, MetricResult[]>
): BulkUpdateHandoffSnapshotRecordEntry[] => {
  return locs.map((loc) => {
    const details = detailsMap[loc.locationId];
    const metrics =
      metricsByLocation?.[loc.locationId]?.map((m) => ({
        section: m.section,
        subsection: m.subsection,
        testCriteria: m.testCriteria,
        expectedValue: String(m.expectedValue ?? ''),
        actualValue: String(m.actualValue ?? ''),
        result: m.result,
        exception: m.exception || '',
      })) || [];

    return {
      handoffSnapshot: {
        id: currentHandoffIdMap[loc.locationId] || '',
        locationId: loc.locationId,
        groupId: currentGroupId,
        pointOfContact: details?.pocEmail || '',
        officeExperience: details?.notes?.officeExperience || '',
        disclaimerTypeSent: details?.notes?.disclaimerTypeSent || '',
        routerMakeAndModel: details?.notes?.routerMakeAndModel || '',
        handoffType: HandoffTypes.OnboardingHandoffUnify,
        handoffName: 'Onboarding Handoff (Unify)',
        metrics,
      },
    };
  });
};
